<template>
  <div class="subscription-terms-container">
    <el-main>
      <div class="terms-content">
        <h1 class="terms-title">Subscription Terms</h1>
        <p class="terms-date">Last Updated: March 2026</p>

        <section class="terms-section">
          <h2>1. Overview</h2>
          <p>These Subscription Terms ("Subscription Terms") govern your purchase and use of the LearningFirst platform subscription service. These terms are in addition to our <router-link to="/terms-of-service">Terms of Service</router-link> and <router-link to="/privacy-policy">Privacy Policy</router-link>.</p>
          <p>By subscribing to LearningFirst, you agree to these Subscription Terms.</p>
        </section>

        <section class="terms-section">
          <h2>2. Subscription Plans</h2>

          <h3>2.1 LearningFirst Monthly ($10/month)</h3>
          <p>The LearningFirst subscription provides access to all premium content, including:</p>
          <ul>
            <li>Unlimited access to all collections and bundles on the platform</li>
            <li>Unlock all exclusive and subscribers-only content within collections</li>
            <li>Access all cryptic (hidden) cards within collections</li>
            <li>New content added regularly by creators</li>
            <li>Cancel anytime</li>
          </ul>

          <h3>2.2 LearningFirst Yearly ($96/year)</h3>
          <p>The yearly plan includes all the same features as the Monthly plan at a discounted rate of $8.00 USD/month (billed annually at $96.00 USD), saving 20% compared to the monthly plan.</p>

          <h3>2.3 7-Day Trial ($1)</h3>
          <p>New users may be eligible for a 7-day trial at a discounted price of $1.00 USD. The trial includes full access to all subscription features. Trial eligibility is limited to one trial per user.</p>
          <ul>
            <li>Full platform access for 7 days</li>
            <li>After the trial period, the subscription automatically converts to the Monthly plan at $10.00 USD/month unless cancelled</li>
          </ul>

          <h3>2.4 Pricing</h3>
          <p>Current subscription prices:</p>
          <ul>
            <li><strong>Monthly:</strong> $10.00 USD per month</li>
            <li><strong>Yearly:</strong> $96.00 USD per year ($8.00 USD/month, 20% savings)</li>
            <li><strong>7-Day Trial:</strong> $1.00 USD (one-time, converts to Monthly after 7 days)</li>
          </ul>
          <p>Prices are subject to change with notice as described in Section 8.</p>
        </section>

        <section class="terms-section">
          <h2>3. Billing and Payment</h2>

          <h3>3.1 Payment Processing</h3>
          <p>All payments are processed securely through Stripe. By subscribing, you authorize us to charge your payment method for the subscription fee and any applicable taxes.</p>

          <h3>3.2 Billing Cycle</h3>
          <p>Your subscription begins on the date of your initial payment and renews automatically on the same date each month (e.g., if you subscribe on March 15, your subscription renews on April 15). For trial subscriptions, the billing cycle begins at the end of the 7-day trial period.</p>

          <h3>3.3 Automatic Renewal</h3>
          <p><strong>YOUR SUBSCRIPTION WILL AUTOMATICALLY RENEW</strong> at the end of each billing period unless you cancel before the renewal date. You will be charged the then-current subscription fee using your stored payment method.</p>

          <h3>3.4 Failed Payments</h3>
          <p>If a payment fails, we may attempt to charge your payment method again. If payment continues to fail, your access to premium features may be suspended until payment is resolved.</p>
        </section>

        <section class="terms-section">
          <h2>4. Trial</h2>
          <p>The 7-day trial at $1.00 USD is available to new subscribers who have not previously used a trial. At the end of the trial period, your subscription will automatically convert to the Monthly plan at $10.00 USD/month unless cancelled before the trial ends. Only one trial is permitted per user account.</p>
        </section>

        <section class="terms-section">
          <h2>5. Cancellation</h2>

          <h3>5.1 How to Cancel</h3>
          <p>You may cancel your subscription at any time through:</p>
          <ul>
            <li>Your account settings on the Platform Subscription page</li>
            <li>The Stripe Customer Portal accessed via "Manage Billing"</li>
          </ul>

          <h3>5.2 Effect of Cancellation</h3>
          <p>When you cancel your subscription:</p>
          <ul>
            <li>Your subscription will not renew at the end of the current billing period</li>
            <li>You will retain access to all subscription features until the end of your paid period</li>
            <li>After the paid period ends, your access to subscribers-only content will be revoked</li>
            <li>Your account, saved progress, and quiz answers will be retained</li>
          </ul>

          <h3>5.3 Reactivation</h3>
          <p>If your subscription is scheduled for cancellation but has not yet expired, you may reactivate it through the Platform Subscription page or the Stripe Customer Portal. If your subscription has already expired, you will need to subscribe again.</p>
        </section>

        <section class="terms-section">
          <h2>6. Refund Policy</h2>

          <h3>6.1 General Policy</h3>
          <p>Subscription fees are generally non-refundable. However, we may provide refunds or credits at our discretion in the case of duplicate charges or billing errors.</p>

          <h3>6.2 Requesting a Refund</h3>
          <p>To request a refund, please contact us at <a href="mailto:contact@eae.international">contact@eae.international</a> with your account email and reason for the request. Refund requests are evaluated on a case-by-case basis.</p>
        </section>

        <section class="terms-section">
          <h2>7. Service Availability</h2>
          <p>We strive to maintain continuous availability of subscription features but do not guarantee uninterrupted service. We are not liable for temporary service interruptions due to maintenance, technical issues, or circumstances beyond our control.</p>
        </section>

        <section class="terms-section">
          <h2>8. Changes to Subscription</h2>

          <h3>8.1 Price Changes</h3>
          <p>We may change subscription prices with at least 30 days' notice before your next billing date. You will have the opportunity to cancel before the new price takes effect. Continued use after a price change constitutes acceptance of the new price.</p>

          <h3>8.2 Feature Changes</h3>
          <p>We may modify, add, or remove subscription features at any time. We will notify subscribers of significant changes that materially reduce the value of the subscription.</p>
        </section>

        <section class="terms-section">
          <h2>9. Account Sharing</h2>
          <p>Your subscription is for your personal use only. You may not share your account credentials or allow others to access subscription features through your account. Violation of this policy may result in account suspension or termination without refund.</p>
        </section>

        <section class="terms-section">
          <h2>10. Termination by LearningFirst</h2>
          <p>We reserve the right to terminate your subscription and access to premium features if you:</p>
          <ul>
            <li>Violate our Terms of Service or these Subscription Terms</li>
            <li>Engage in fraudulent activity or abuse of the service</li>
            <li>Fail to pay subscription fees</li>
          </ul>
          <p>In cases of termination for cause, no refund will be provided. In cases of termination without cause by LearningFirst, we will provide a pro-rata refund for the unused portion of your subscription.</p>
        </section>

        <section class="terms-section">
          <h2>11. Minors</h2>
          <p>If you are under 18 years old, any subscription purchase must be approved and made by a parent or legal guardian. We are not liable for unauthorized subscription purchases made by minors without parental consent. Please refer to our <router-link to="/terms-of-service">Terms of Service</router-link> for full eligibility requirements.</p>
        </section>

        <section class="terms-section">
          <h2>12. Taxes</h2>
          <p>Subscription fees do not include taxes. You are responsible for any applicable sales tax, VAT, GST, or other taxes based on your location. Taxes will be calculated and displayed at checkout.</p>
        </section>

        <section class="terms-section">
          <h2>13. Contact</h2>
          <p>For billing questions or subscription support, please contact:</p>
          <div class="contact-info">
            <p><strong>Extracurricular Activity Excellence International Pte. Ltd.</strong></p>
            <p>Email: <a href="mailto:contact@eae.international">contact@eae.international</a></p>
          </div>
        </section>

        <div class="footer-links">
          <router-link to="/terms-of-service">Terms of Service</router-link>
          <router-link to="/privacy-policy">Privacy Policy</router-link>
        </div>
      </div>
    </el-main>
  </div>
</template>

<script>
export default {
  name: 'SubscriptionTerms',
  meta: {
    title: 'Subscription Terms - LearningFirst'
  }
}
</script>

<style scoped>
.subscription-terms-container {
  width: 100%;
  min-height: 100vh;
  background-color: #f5f7fa;
  padding-top: 60px;
}

.terms-content {
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  line-height: 1.6;
  color: #303133;
}

.terms-title {
  font-size: 2.5rem;
  font-weight: bold;
  color: #1159AF;
  margin-bottom: 10px;
  text-align: center;
}

.terms-date {
  font-size: 1.1rem;
  color: #606266;
  text-align: center;
  margin-bottom: 40px;
}

.terms-section {
  margin-bottom: 40px;
}

.terms-section h2 {
  font-size: 1.8rem;
  font-weight: bold;
  color: #1159AF;
  margin-bottom: 15px;
  border-bottom: 2px solid #e4e7ed;
  padding-bottom: 8px;
}

.terms-section h3 {
  font-size: 1.4rem;
  font-weight: 600;
  color: #303133;
  margin: 25px 0 15px 0;
}

.terms-section p {
  margin-bottom: 15px;
  font-size: 1rem;
  color: #606266;
}

.terms-section ul {
  margin: 15px 0;
  padding-left: 20px;
}

.terms-section li {
  margin-bottom: 8px;
  color: #606266;
}

.terms-section strong {
  color: #303133;
  font-weight: 600;
}

.terms-section a {
  color: #409EFF;
  text-decoration: none;
}

.terms-section a:hover {
  text-decoration: underline;
}

.contact-info {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 6px;
  border-left: 4px solid #409EFF;
  margin-top: 15px;
}

.contact-info p {
  margin-bottom: 8px;
}

.footer-links {
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-top: 40px;
  padding-top: 20px;
  border-top: 1px solid #e4e7ed;
}

.footer-links a {
  color: #409EFF;
  text-decoration: none;
  font-size: 14px;
}

.footer-links a:hover {
  text-decoration: underline;
}

@media (max-width: 768px) {
  .terms-content {
    padding: 20px 15px;
    margin: 0 10px;
  }

  .terms-title {
    font-size: 2rem;
  }

  .terms-section h2 {
    font-size: 1.5rem;
  }

  .terms-section h3 {
    font-size: 1.2rem;
  }
}
</style>
